import React, { useEffect, useState } from 'react';
import { Input, Checkbox, DatePicker, Button } from 'antd';
import { renderNameFilters } from '../AttributeFilterUtils';
import DateFilterComponent from './DateFilterComponent';
import BinnedDateComponent from './complianceCheck'; // Corrected import name based on common Ant Design naming and previous context
import { CloseOutlined } from '@ant-design/icons';

export const handleCurrentFilter = (
  currentFilter,
  initiatives,
  attributeFilters,
  setAttributeFilters,
  searchTerm,
  setSearchTerm,
  determineStage,
  initiativeOwnerNames,
  initiativeBackupNames,
  workstreamSponsorNames,
  workstreamLeadNames,
  workstreamSponsorWorkstreamNames,
  AllowOnlyTo,
  crossFunctionalSupportWorkstream,
  strategyInfo,
  Subworkstream,
  users,
  subsectors
) => {
  const dateFilterKeys = [
    'L1 planned date',
    'L1 Original Planned Date',
    'L2 Original Planned Date',
    'L3 Original Planned Date',
    'L4 Original Planned Date',
    'L5 Original Planned Date',
    'L2 planned date',
    'L3 planned date',
    'L4 planned date',
    'L5 planned date',
    'L0 actual date',
    'L1 actual date',
    'L2 actual date',
    'L3 actual date',
    'L4 actual date',
    'L5 actual date',
    'Latest actual date (All L gates)',
    'Sub . for L1 approv . date',
    'Sub . for L2 approv . date',
    'Sub . for L3 approv . date',
    'Sub . for L4 approv . date',
    'Sub . back to L3 approv . date',
    'Sub . for L5 approv . date',
    'Cancellation date',
    'Earliest Milestone start date',
    'Latest Milestone end date',
    '(Target) L4 date',
    'Earliest Milestone start date',
    '(LW) L3 plan date',
    '(LW) L4 plan date',
    'Updated on (incl. Initiative, Milestone, impact, etc.)',
    'Updated on',
  ];

  const handleClearFilters = () => {
    setAttributeFilters({});
    setSearchTerm(''); // Clear search term when filters are cleared
  };

  const clearButton = Object.keys(attributeFilters).length > 0 && (
    <Button
      icon={<CloseOutlined />}
      onClick={handleClearFilters}
      style={{ marginBottom: '10px', color: 'blue' }}
    >
      Clear Filters
    </Button>
  );

  const isNullOrUndefined = (val) => val === null || val === undefined;

  const renderCheckboxFilter = (
    uniqueValues, // Array of all possible unique options for the filter
    filterKey,    // The key used in `attributeFilters` (e.g., 'stage', 'selectedForToMeetingDescusion')
    displayLimit = null, // Optional: number of items to display initially before showing "more"
    isBooleanFilter = false   // Flag for boolean-based filters (e.g., "Has qualitative KPI?", "hasFinancialImpact")
  ) => {
    // normalizeValue is used for filtering unique values based on search term.
    // When rendering, we'll explicitly use item.value for display.
    const normalizeValue = (item) => (isBooleanFilter ? item.value : String(item));
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    // Filter unique values based on the search term
    const filteredUniqueValues = searchTerm
      ? uniqueValues.filter((item) =>
        normalizeValue(item).toLowerCase().includes(lowerCaseSearchTerm)
      )
      : uniqueValues;

    // Determine values to display, respecting the displayLimit and search term
    const valuesToDisplay =
      displayLimit && !searchTerm && filteredUniqueValues.length > displayLimit
        ? filteredUniqueValues.slice(0, displayLimit)
        : filteredUniqueValues;

    const actualRemainingValues =
      displayLimit && !searchTerm && filteredUniqueValues.length > displayLimit
        ? filteredUniqueValues.slice(displayLimit)
        : [];

    // Get the currently applied filter values from `attributeFilters`
    const currentFilterValues = attributeFilters[filterKey];

    // --- Logic for "Select all" checkbox state (checked/indeterminate/unchecked) ---

    let allSelected;
    let isIndeterminate;

    // "Select all" is checked if the filter is undefined (initial/cleared state)
    // or if all currently filtered/searchable options are explicitly selected.
    if (isNullOrUndefined(currentFilterValues)) {
      allSelected = true;
      isIndeterminate = false;
    } else {
      // If filter is an array, compare its contents to `filteredUniqueValues`
      const selectedCount = currentFilterValues.length;
      const totalCount = filteredUniqueValues.length;

      // `allSelected` is true if all filtered unique values are present in the current filter array
      allSelected =
        totalCount > 0 && // Ensure there are options to select
        selectedCount === totalCount &&
        filteredUniqueValues.every((item) => {
          const valueToCheck = isBooleanFilter ? item.filterValue : item;
          return currentFilterValues.includes(valueToCheck);
        });

      // `isIndeterminate` if some items are selected, but not all (and not none).
      isIndeterminate = selectedCount > 0 && selectedCount < totalCount;
    }

    // --- Event Handlers ---

    // Handles checking/unchecking the main "Select all" checkbox
    const handleSelectAllChange = (event) => {
      setAttributeFilters((prevFilters) => ({
        ...prevFilters,
        // If "Select all" is checked, set filter to `undefined` (represents 'show all').
        // If unchecked, set to an empty array `[]` (represents 'show none').
        [filterKey]: event.target.checked ? undefined : [],
      }));
    };

    // Handles checking/unchecking individual item checkboxes
    const handleItemCheckboxChange = (item, checked) => {
      let newFilterValues;
      const itemValueToStore = isBooleanFilter ? item.filterValue : item; // Use filterValue for internal storage
      const currentStoredValues = attributeFilters[filterKey];

      // Determine the base array of values to work with
      // If filter is undefined (all selected), start with all filtered unique values.
      // Otherwise, start with the current stored values.
      const baseValues = isNullOrUndefined(currentStoredValues)
        ? filteredUniqueValues.map(option => isBooleanFilter ? option.filterValue : option)
        : [...currentStoredValues]; // Ensure it's a new array to avoid direct mutation

      if (checked) {
        // Add the item if it's not already there
        newFilterValues = [...new Set([...baseValues, itemValueToStore])];
      } else {
        // Remove the item
        newFilterValues = baseValues.filter((val) => val !== itemValueToStore);
      }

      setAttributeFilters((prevFilters) => ({
        ...prevFilters,
        // If the resulting selection becomes empty, explicitly set to empty array []
        // to represent "none selected," preventing reversion to "select all" (undefined).
        [filterKey]: newFilterValues.length === 0 ? [] : newFilterValues,
      }));
    };

    // --- Rendered JSX ---
    return (
      <div style={{ marginTop: '10px' }}>
        {clearButton}
        <Input
          placeholder="Search"
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: '10px' }}
          value={searchTerm}
        />
        {/* Only show "Select All" checkbox if there are options available to filter */}
        {filteredUniqueValues.length > 0 && (
          <Checkbox
            indeterminate={isIndeterminate}
            checked={allSelected}
            onChange={handleSelectAllChange}
          >
            (Select all)
          </Checkbox>
        )}

        {/* Render individual filter options */}
        {valuesToDisplay.map((item) => {
          // Determine if an individual checkbox should be checked
          const itemValueForFilter = isBooleanFilter ? item.filterValue : item;
          // Check if currentFilterValues is undefined (meaning 'select all'),
          // or if it's an array and contains the item's filterValue.
          const isItemChecked =
            isNullOrUndefined(currentFilterValues) ||
            (Array.isArray(currentFilterValues) && currentFilterValues.includes(itemValueForFilter));

          return (
            <div key={isBooleanFilter ? String(item.filterValue) : String(item)}>
              <Checkbox
                checked={isItemChecked}
                onChange={(e) => handleItemCheckboxChange(item, e.target.checked)}
              >
                {/* Ensure item.value is always used for display for objects, otherwise stringify */}
                {typeof item === 'object' && item !== null && 'value' in item ? item.value : String(item)}
              </Checkbox>
            </div>
          );
        })}

        {/* Conditional messages for search results and display limits */}
        {searchTerm && filteredUniqueValues.length === 0 && (
          <div style={{ marginTop: '10px', color: 'gray' }}>No matching results.</div>
        )}

        {actualRemainingValues.length > 0 && !searchTerm && (
          <div style={{ marginTop: '10px', color: 'gray' }}>
            Showing first {displayLimit} items. Use search to find others.
          </div>
        )}
      </div>
    );
  };

  // Date Filters
  if (dateFilterKeys.includes(currentFilter)) {
    return (
      <DateFilterComponent
        currentFilter={currentFilter}
        attributeFilters={attributeFilters}
        setAttributeFilters={setAttributeFilters}
      />
    );
  }

  // Binned Date Filters (Compliance Checks)
  if (
    currentFilter === 'L4 date compliance check (forecast vs planned date)' ||
    currentFilter === 'L3 date compliance check (forecast vs planned date)'
  ) {
    return (
      <BinnedDateComponent
        currentFilter={currentFilter}
        attributeFilters={attributeFilters}
        setAttributeFilters={setAttributeFilters}
      />
    );
  }

  // TO Finance Pending Approvers
  if (currentFilter === 'TO finance pending approvers') {
    const toFinanceUsers = users
      .filter((user) => user.role.name === 'TO Finance')
      .map((user) => `${user.firstName} ${user.lastName}`);
    return (
      <>
        {clearButton}
        {renderNameFilters(
          toFinanceUsers,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }
  if (currentFilter === 'Strategy analyst pending approvers') {
    const toFinanceUsers = users
      .filter((user) => user.role.name === 'Strategy Analyst')
      .map((user) => `${user.firstName} ${user.lastName}`);
    return (
      <>
        {clearButton}
        {renderNameFilters(
          toFinanceUsers,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }
  // Benefit and Cost Filters (predefined ranges)
  const benefitAndCostFilters = [
    'Change in net recurring benefits (binned, current vs last week, latest estimate, annualized)',
    'Change in One Time Benefit (binned, current vs last week, latest estimate, annualized)',
    'Change in implementation costs (binned, latest estimate, annualized)',
  ];
  if (benefitAndCostFilters.includes(currentFilter)) {
    const benefitRanges = [
      '<not set>',
      'Increased by > 1 ETB m',
      'Increased by 0.1-1 ETB m',
      'Increased by < 0.1 ETB m',
      'Unchanged',
      'Decreased by < 0.1 ETB m',
      'Decreased by 0.1-1 ETB m',
      'Decreased by > 1 ETB m',
    ];
    return renderCheckboxFilter(benefitRanges, currentFilter);
  }

  // Pipeline Variance Filters (predefined options)
  const PipelineFilter = [
    'Pipeline weekly variance (L0+)',
    'Pipeline weekly variance (L1+)',
    'Pipeline weekly variance (L2+)',
    'Pipeline weekly variance (L3+)',
    'Pipeline weekly variance (L4+)',
  ];
  if (PipelineFilter.includes(currentFilter)) {
    const options = [
      '<not set>',
      'Increased value',
      'New Initiative',
      'Decreased value',
      'Cancelled/On hold',
      'Unchanged',
    ];
    return renderCheckboxFilter(options, currentFilter);
  }

  // FY 2024/25 actual impact update check (predefined options)
  if (currentFilter === 'FY 2024/25 actual impact update check') {
    const impactUpdateOptions = [
      '<not set>',
      'No actuals (below L3 or cancelled/on hold)',
      'No actuals required or no impacts',
      'Missing actual in last completed month',
      'Missing actual in previous months',
      'Missing actual in last completed and previous completed months',
      'Filled actual (last completed month required only)',
      'Filled actual (previous months required only)',
      'Filled actual (last completed and previous months required)',
    ];
    return renderCheckboxFilter(impactUpdateOptions, currentFilter);
  }

  // Handle nested object filtering (e.g., "workstreams.name")
  if (currentFilter.includes('.')) {
    const [parentKey, childKey] = currentFilter.split('.');
    const uniqueChildValues = [
      ...new Set(
        initiatives
          .flatMap((i) => i[parentKey]?.map((item) => item[childKey]) || [])
          .filter(Boolean)
      ),
    ];
    return renderCheckboxFilter(uniqueChildValues, currentFilter);
  }

  // Name Filters (re-using renderNameFilters for specific person/workstream types)
  if (currentFilter === 'initiativeOwnerInfo') {
    return (
      <>
        {clearButton}
        {renderNameFilters(
          initiativeOwnerNames,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }

  if (currentFilter === 'initiativeOwnerBackupInfo') {
    return (
      <>
        {clearButton}
        {renderNameFilters(
          initiativeBackupNames,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }

  if (
    currentFilter === 'workstreamSponsorInfo' ||
    currentFilter === 'Workstream sponsor pending approvers'
  ) {
    return (
      <>
        {clearButton}
        {renderNameFilters(
          workstreamSponsorNames,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }

  if (
    currentFilter === 'workstreamLeadInfo' ||
    currentFilter === 'Workstream lead pending approvers'
  ) {
    return (
      <>
        {clearButton}
        {renderNameFilters(
          workstreamLeadNames,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }

  if (currentFilter === 'workstreamSponsorWorkstream') {
    return (
      <>
        {clearButton}
        {renderNameFilters(
          workstreamSponsorWorkstreamNames,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }

  if (currentFilter === 'AllowOnlyTo') {
    return (
      <>
        {clearButton}
        {renderNameFilters(
          AllowOnlyTo,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }

  if (currentFilter === 'crossFunctionalSupportWorkstreamInfo') {
    return (
      <>
        {clearButton}
        {renderNameFilters(
          crossFunctionalSupportWorkstream,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }
  if (currentFilter === 'strategyInfo') {
    return (
      <>
        {clearButton}
        {renderNameFilters(
          strategyInfo,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }

  // Benefit/Cost Range Filters
  if (
    currentFilter === 'Net recurring benefit range' ||
    currentFilter === 'Future implementation costs range'
  ) {
    const benefitRanges = [
      '<not set>',
      'No value',
      'Negative value',
      'ETB 0-ETB 1Mn',
      'ETB 1Mn-ETB 10Mn',
      'ETB 10Mn-ETB 50Mn',
      'ETB 50Mn-ETB 100Mn',
      'ETB 100Mn-ETB 500Mn',
      'ETB 500Mn-ETB 1Bn',
      '>ETB 1Bn',
    ];
    return renderCheckboxFilter(benefitRanges, currentFilter);
  }

  if (currentFilter === 'Strategy analyst pending approvers') {
    const strategyAnalystUsers = users
      .filter((user) => user.role.name === 'Strategy Analyst')
      .map((user) => `${user.firstName} ${user.lastName}`);
    return (
      <>
        {clearButton}
        {renderNameFilters(
          strategyAnalystUsers,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }

  // Actual Impact/KPI Update Check Filters
  if (
    currentFilter === 'Actual impact update check (for last completed and previous period)' ||
    currentFilter === 'Actual KPI update check (for last completed and previous period)'
  ) {
    const filterOptions = [
      '<not set>',
      'No actuals (below L3 or cancelled/on hold)',
      'No actuals required or no impacts',
      'Missing actual in last completed month',
      'Missing actual in previous months',
      'Missing actual in last completed and previous completed months',
      'Filled actual (last completed month required only)',
      'Filled actual (previous months required only)',
      'Filled actual (last completed and previous months required)',
    ];
    return renderCheckboxFilter(filterOptions, currentFilter);
  }
  if (currentFilter === '(FY24-25) Actual impact update check') {
    const filterOptions = [
      '<not set>',
      'No actuals (below L3 or cancelled/on hold)',
      'No actuals required or no impacts',
      'Missing actual in FY 24-25',
      'Filled actual in FY 24-25',
    ];
    return renderCheckboxFilter(filterOptions, currentFilter);
  }
  if (currentFilter === '(FY25-26) Actual impact update check') {
    const filterOptions = [
      '<not set>',
      'No actuals (below L3 or cancelled/on hold)',
      'No actuals required or no impacts',
      'Missing actual in FY 25-26',
      'Filled actual in FY 25-26',
    ];
    return renderCheckboxFilter(filterOptions, currentFilter);
  }
  if (currentFilter === '(FY23-24) Actual impact update check') {
    const filterOptions = [
      '<not set>',
      'No actuals (below L3 or cancelled/on hold)',
      'No actuals required or no impacts',
      'Missing actual in FY 23-24',
      'Filled actual in FY 23-24',
    ];
    return renderCheckboxFilter(filterOptions, currentFilter);
  }

  // Approval Status Filters
  if (
    currentFilter === 'Workstream lead pending approval' ||
    currentFilter === 'Workstream sponsor pending approval' ||
    currentFilter === 'TO finance pending approval' ||
    currentFilter === 'Strategy analyst pending approval'
  ) {
    const uniqueApprovalStatuses = ['<not set>', 'Pending', 'Approved'];
    return renderCheckboxFilter(uniqueApprovalStatuses, currentFilter);
  }

  // Weekly Status Filter
  if (currentFilter === '(LW) Weekly status') {
    const uniqueStatuses = [
      '<not set>',
      'On track',
      'Some issues',
      'Leadership attention',
      'On hold',
      'Cancelled',
      'Rejected',
    ];
    return renderCheckboxFilter(uniqueStatuses, currentFilter);
  }

  // Stage Filters
  if (currentFilter === '(LW) Stage') {
    const uniqueStages = [
      '<not set>',
      'LO (Idea)',
      'Submitted for L1 approval',
      'L1 (Identified)',
      'Submitted for L2 approval',
      'L2 (Validated)',
      'Submitted for L3 approval',
      'L3 (Planned)',
      'Submitted for L4 approval',
      'Submitted for return to L3 approval',
      'L4 (Executed)',
      'Submitted for L5 approval',
      'L5 (Realised)',
    ];
    return renderCheckboxFilter(uniqueStages, currentFilter);
  }

  if (currentFilter === 'Planning summary' || currentFilter === '(LW) Planning summary') {
    const planningSummaries = [
      '<not set>',
      'Past due for L5',
      'Past due for L4',
      'Past due for L3',
      'Past due for L2',
      'Due soon for L5',
      'Due soon for L4',
      'Due soon for L3',
      'Due soon for L2',
      'Due soon for L2 for 30 days',
      'Due soon for L3 for 30 days',
      'Due soon for L4 for 30 days',
      'Due soon for L5 for 30 days',
      'Not planned (L2 to L4)',
      'Not planned (L5)',
      'On plan',
      'Realized',
      'Cancelled',
    ];
    return renderCheckboxFilter(planningSummaries, currentFilter);
  }
  if (currentFilter === 'Planning summary simplified') {
    const planningSummaries = [
      '<not set>',
      'Past due',
      'Due soon',
      'Due soon In 30 days',
      'Not planned',
      'On plan',
      'Realized',
      'Cancelled',
    ];
    return renderCheckboxFilter(planningSummaries, currentFilter);
  }
  if (currentFilter === 'stage') {
    const stageOptions = [
      'L0',
      'L1',
      'L2',
      'L3',
      'L4',
      'L5'
    ]
    return renderCheckboxFilter(stageOptions, currentFilter)
  }
  if (currentFilter === 'stageSimplified') {
    const stageOptions = [
      'L0',
      'Submitted for L1 Approval',
      'L1',
      'Submitted for L2 Approval',
      'L2',
      'Submitted for L3 Approval',
      'L3',
      'Submitted for L4 Approval',
      'L4',
      'Submitted for L5 Approval',
      'L5'
    ]
    return renderCheckboxFilter(stageOptions, currentFilter)
  }
  // Number of attachments (special handling for counts)
  if (currentFilter === 'Number of attachments') {
    const attachmentCounts = initiatives.reduce((acc, initiative) => {
      const count = initiative.attachmentCount || 0;
      acc[count] = (acc[count] || 0) + 1;
      return acc;
    }, {});

    const options = Object.entries(attachmentCounts)
      .map(([count, initiativeCount]) => ({
        value: parseInt(count),
        label: `${count} (${initiativeCount})`,
      }))
      .sort((a, b) => a.value - b.value);
    const allSelected =
      attributeFilters[currentFilter] === undefined ||
      (attributeFilters[currentFilter] && attributeFilters[currentFilter].length === options.length);
    const isIndeterminate =
      attributeFilters[currentFilter] !== undefined &&
      attributeFilters[currentFilter].length > 0 &&
      attributeFilters[currentFilter].length < options.length;

    const handleAttachmentSelectAll = (e) => {
      setAttributeFilters((prevFilters) => ({
        ...prevFilters,
        [currentFilter]: e.target.checked ? undefined : [],
      }));
    };

    const handleAttachmentCheckboxChange = (optionValue, checked) => {
      let currentAttachmentFilters = attributeFilters[currentFilter];

      // If `currentAttachmentFilters` is undefined (meaning "Select all"),
      // the base for modification should be an array containing all possible option values.
      const baseValues = isNullOrUndefined(currentAttachmentFilters)
        ? options.map(opt => opt.value)
        : [...currentAttachmentFilters];

      let newFilterValues;
      if (checked) {
        newFilterValues = [...new Set([...baseValues, optionValue])];
      } else {
        newFilterValues = baseValues.filter((val) => val !== optionValue);
      }

      setAttributeFilters((prevFilters) => ({
        ...prevFilters,
        [currentFilter]: newFilterValues.length === 0 ? [] : newFilterValues,
      }));
    };

    return (
      <div style={{ padding: '10px' }}>
        {clearButton}
        <Input
          placeholder="Search"
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: '10px' }}
          value={searchTerm}
        />
        <Checkbox
          indeterminate={isIndeterminate}
          checked={allSelected}
          onChange={handleAttachmentSelectAll}
        >
          (Select all)
        </Checkbox>
        {options
          .filter((option) => String(option.label).toLowerCase().includes(searchTerm.toLowerCase()))
          .map((option) => {
            const isOptionChecked =
              allSelected ||
              (attributeFilters[currentFilter] && attributeFilters[currentFilter].includes(option.value));

            return (
              <div key={option.value}>
                <Checkbox
                  checked={isOptionChecked}
                  onChange={(e) => handleAttachmentCheckboxChange(option.value, e.target.checked)}
                >
                  {option.label}
                </Checkbox>
              </div>
            );
          })}
      </div>
    );
  }

  if (currentFilter === 'workstreamLeadSubworkstream') {
    return (
      <>
        {clearButton}
        {renderNameFilters(
          Subworkstream,
          currentFilter,
          attributeFilters,
          setAttributeFilters,
          searchTerm,
          setSearchTerm
        )}
      </>
    );
  }
  if (currentFilter === 'sectorInfo') {
    const sectorNames = initiatives.flatMap(item => {
      if (!Array.isArray(item?.sectorAccounts) || item.sectorAccounts.length === 0) {
        return ['<not set>'];
      }

      return item?.sectorAccounts
        .map(sec => {
          // Find the sector object by ID from your sectors array
          const sectorObj = subsectors.find(s => s._id === sec.sector);
          return sectorObj?.sector?.sectorName || '<not set>';
        })
        .filter(Boolean);
    });

    const uniqueSectors = [...new Set(sectorNames)];

    return renderCheckboxFilter(uniqueSectors, currentFilter);
  }

  if (currentFilter === 'subsectorInfo') {
    const subsectorNames = initiatives.flatMap(item => {
      if (!Array.isArray(item?.sectorAccounts) || item.sectorAccounts.length === 0) {
        return ['<not set>'];
      }
      return item?.sectorAccounts
        .map(sec => {
          const sectorObj = subsectors.find(s => s._id === sec.sector);
          return sectorObj?.subsectorName || '<not set>';
        })
        .filter(Boolean);
    });

    const uniqueSubsectors = [...new Set(subsectorNames)];

    return renderCheckboxFilter(uniqueSubsectors, currentFilter);
  }
  if (currentFilter === 'Type') {
    const initiativeTypes = initiatives.flatMap(item => {
      return item?.type ? item.type : '<not set>';
    });

    const uniqueType = [...new Set(initiativeTypes)];
    return renderCheckboxFilter(uniqueType, currentFilter);
  }
  // --- Default Case for other filters ---
  const getUniqueValues = (initiatives, filterKey) => {
    if (filterKey === 'initiativeNumber - initiativeName') {
      return [
        ...new Set(
          initiatives.map((i) => `${i.initiativeNumber} - ${i.initiativeName}`).filter(Boolean)
        ),
      ];
    }
    else if (filterKey === 'Description') {
      return [
        ...new Set(
          initiatives.map((i) => i.description).filter(Boolean)
        ),
      ];
    }
    else if (filterKey === 'Has qualitative KPI?') {
      // INVERTED LOGIC: 'Yes' filters for false, 'No' filters for true
      return [
        { value: '<not set>', filterValue: '<not set>' },
        { value: 'Yes', filterValue: false }, // 'Yes' means DOES NOT have qualitative KPI
        { value: 'No', filterValue: true },  // 'No' means DOES have qualitative KPI
      ];
    } else if (filterKey === 'hasFinancialImpact') {
      return [
        { value: 'Yes', filterValue: true },
        { value: 'No', filterValue: false },
      ];
    }
    else if (filterKey === 'forSpecificCustomer') {
      return [
        { value: 'Yes', filterValue: true },
        { value: 'No', filterValue: false }
      ];
    }
    else if (filterKey === 'segmentalPBT') {
      return [
        { value: 'Yes', filterValue: true },
        { value: 'No', filterValue: false }
      ]
    }
    else if (filterKey === 'selectedForToMeetingDescusion') {
      return [
        { value: 'Yes', filterValue: true },
        { value: 'No', filterValue: false },
      ];
    }
    else if (filterKey === 'owner') {
      return [...new Set(initiatives.map((i) => i.owner?.fullName).filter(Boolean))];
    } else if (filterKey === 'accountableWorkstream') {
      return [...new Set(initiatives.map((i) => i.accountableWorkstream?.name).filter(Boolean))];
    } else if (filterKey === 'Subworkstream') {
      return [...new Set(initiatives.map((i) => i.subWorkstream?.name).filter(Boolean))];
    } else if (filterKey === 'sponsor') {
      return [...new Set(initiatives.map((i) => i.sponsor?.fullName).filter(Boolean))];
    } else if (filterKey === 'type') {
      return [...new Set(initiatives.map((i) => i.type).filter(Boolean))];
    } else if (filterKey === 'budget') {
      return [...new Set(initiatives.map((i) => i.budget).filter(Boolean))];
    } else if (filterKey === 'lastUpdatedBy') {
      return [...new Set(initiatives.map((i) => i.lastUpdatedBy?.fullName).filter(Boolean))];
    } else {
      return [...new Set(initiatives.map((i) => i[filterKey]).filter(Boolean))];
    }
  };

  const uniqueValues = getUniqueValues(initiatives, currentFilter);

  // Apply the reusable renderCheckboxFilter based on the currentFilter
  if (currentFilter === 'Has qualitative KPI?' || currentFilter === 'hasFinancialImpact' || currentFilter === 'selectedForToMeetingDescusion' || currentFilter === 'forSpecificCustomer' || currentFilter === 'segmentalPBT') {
    return renderCheckboxFilter(uniqueValues, currentFilter, null, true); // No display limit, special boolean handling
  }
  else if (
    currentFilter === 'initiativeName' ||
    currentFilter === 'initiativeNumber' ||
    currentFilter === 'initiativeNumber - initiativeName' ||
    currentFilter === 'Description'
  ) {
    // Setting displayLimit to 60 for these filters to improve performance
    return renderCheckboxFilter(uniqueValues, currentFilter, 60);
  } else {
    // Default case for other single-select checkbox fields (no limit by default)
    return renderCheckboxFilter(uniqueValues, currentFilter);
  }
};